import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../api';
import type { CallLogEntry } from '../types';
import { useChat } from '../store';
import { Avatar } from './Avatar';
import {
  IconClose,
  IconMoreVertical,
  IconPhone,
  IconPhoneIncoming,
  IconPhoneMissed,
  IconPhoneOutgoing,
  IconPlus,
  IconRefresh,
  IconVideo,
} from './Icon';
import { relativeTime } from '../utils/time';
import { NewChatModal } from './NewChatModal';
import type { ActiveCallInfo } from './CallModal';

interface CallsPageProps {
  onStartCall?: (info: ActiveCallInfo) => void;
}

function groupBucket(iso: string): string {
  const t = new Date(iso).getTime();
  const now = Date.now();
  const ageHours = (now - t) / (1000 * 60 * 60);
  if (ageHours < 24) return 'Today';
  if (ageHours < 48) return 'Yesterday';
  if (ageHours < 24 * 7) return 'This week';
  if (ageHours < 24 * 30) return 'This month';
  return 'Earlier';
}

const ORDER = ['Today', 'Yesterday', 'This week', 'This month', 'Earlier'];

export function CallsPage({ onStartCall }: CallsPageProps) {
  const me = useChat((s) => s.me);
  const conversations = useChat((s) => s.conversations);
  const selectConversation = useChat((s) => s.selectConversation);
  const users = useChat((s) => s.users);

  const [calls, setCalls] = useState<CallLogEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [callError, setCallError] = useState<string | null>(null);
  const [pendingCallback, setPendingCallback] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'missed'>('all');
  const [detailsCall, setDetailsCall] = useState<CallLogEntry | null>(null);

  async function load(opts: { silent?: boolean } = {}) {
    if (!opts.silent) setLoading(true);
    setRefreshing(true);
    try {
      const res = await api.callHistory();
      setCalls(res.calls);
    } catch {
      setCalls([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function callBack(convId: string, video: boolean) {
    setCallError(null);
    setPendingCallback(`${convId}:${video ? 'v' : 'a'}`);
    try {
      if (onStartCall) {
        const res = await api.startCall(convId, video);
        onStartCall({
          roomName: res.roomName,
          conversationId: convId,
          url: res.url,
          token: res.token,
          video,
        });
      } else {
        // Fallback: just navigate to the chat — user can hit the call button.
        selectConversation(convId);
      }
    } catch (err) {
      setCallError(
        err instanceof ApiError && err.code === 'calls_not_configured'
          ? 'Calls require LiveKit setup.'
          : 'Could not start the call.',
      );
      window.setTimeout(() => setCallError(null), 4000);
    } finally {
      setPendingCallback(null);
    }
  }

  // Determines whether a call counts as "missed" for the current user. Same
  // rule used by row rendering — extracted so the missed-only filter and
  // header counter share a single source of truth.
  function isMissedCall(c: CallLogEntry): boolean {
    return (
      c.startedBy !== me?.id &&
      !c.participants.some((p) => p.userId === me?.id && p.joinedAt)
    );
  }

  // Group + sort once. Groups appear in ORDER; entries inside a group keep
  // server order (most recent first). Honors the missed-only filter.
  const grouped = useMemo(() => {
    if (!calls) return null;
    const filtered = filter === 'missed' ? calls.filter(isMissedCall) : calls;
    const buckets = new Map<string, CallLogEntry[]>();
    for (const c of filtered) {
      const k = groupBucket(c.startedAt);
      const arr = buckets.get(k) ?? [];
      arr.push(c);
      buckets.set(k, arr);
    }
    return ORDER.filter((k) => buckets.has(k)).map((k) => ({
      heading: k,
      items: buckets.get(k)!,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calls, filter, me?.id]);

  const totalCount = calls?.length ?? 0;
  const missedCount = useMemo(() => {
    if (!calls) return 0;
    return calls.filter(isMissedCall).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calls, me?.id]);

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Glass header — matches ChatHeader chrome. */}
      <header className="sticky top-0 z-10 glass-chrome hairline px-6 py-4 flex items-center justify-between shrink-0">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-zinc-500 mb-0.5">
            Activity
          </p>
          <h1 className="text-lg font-semibold tracking-tight">Calls</h1>
        </div>
        <div className="flex items-center gap-2">
          {totalCount > 0 && (
            <div className="flex items-center gap-1.5 text-[11px] text-zinc-500 mr-2">
              <span className="tabular-nums">{totalCount} total</span>
              {missedCount > 0 && (
                <>
                  <span className="text-zinc-700">·</span>
                  <span className="tabular-nums text-rose-400">{missedCount} missed</span>
                </>
              )}
            </div>
          )}
          {missedCount > 0 && (
            <div className="rounded-full hairline bg-zinc-900/60 p-0.5 flex items-center text-[11px] font-medium">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 rounded-full transition ${
                  filter === 'all'
                    ? 'bg-zinc-50 text-zinc-950 shadow-inset-hairline-strong'
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('missed')}
                className={`px-3 py-1 rounded-full transition ${
                  filter === 'missed'
                    ? 'bg-rose-500/20 text-rose-200'
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
              >
                Missed
              </button>
            </div>
          )}
          <button
            onClick={() => void load()}
            disabled={refreshing}
            title="Refresh"
            aria-label="Refresh calls"
            className="group w-9 h-9 rounded-xl bg-zinc-900/60 hover:bg-zinc-900 hairline text-zinc-400 hover:text-zinc-100 disabled:opacity-50 transition-all duration-300 ease-spring active:scale-[0.94] flex items-center justify-center"
          >
            <IconRefresh
              size={14}
              className={`transition-transform duration-700 ease-spring ${
                refreshing ? 'animate-spin' : 'group-hover:rotate-90'
              }`}
            />
          </button>
          <button
            onClick={() => setNewChatOpen(true)}
            className="group flex items-center gap-2 rounded-full hairline-strong bg-zinc-900 hover:bg-zinc-800 px-3.5 py-1.5 text-xs font-medium text-zinc-100 transition-all duration-300 ease-spring active:scale-[0.96]"
          >
            <IconPlus size={12} className="transition-transform duration-300 ease-spring group-hover:rotate-90" />
            <span>Start a call</span>
          </button>
        </div>
      </header>

      {callError && (
        <div className="px-6 py-2 animate-fade-up">
          <p className="text-xs text-rose-400 bg-rose-500/10 hairline rounded-md px-3 py-2">
            {callError}
          </p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {loading && (
          <p className="text-center text-xs text-zinc-500 py-12">Loading…</p>
        )}

        {!loading && calls !== null && calls.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 px-6 text-center animate-fade-up">
            <div className="size-14 rounded-full bg-zinc-900/60 hairline flex items-center justify-center text-zinc-500 mb-4">
              <IconPhone size={20} strokeWidth={1.5} />
            </div>
            <h2 className="text-base font-semibold tracking-tight mb-1.5">No calls yet</h2>
            <p className="text-xs text-zinc-500 max-w-xs leading-relaxed">
              Start a call from any conversation by clicking the phone or video
              icon in the chat header — or use{' '}
              <button
                onClick={() => setNewChatOpen(true)}
                className="text-zinc-300 underline underline-offset-2 hover:text-zinc-100 transition-colors"
              >
                Start a call
              </button>{' '}
              above.
            </p>
          </div>
        )}

        {!loading && grouped && (
          <div className="px-3 py-3 space-y-5">
            {grouped.map((bucket) => (
              <section key={bucket.heading}>
                <h3 className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.14em] font-medium text-zinc-500">
                  {bucket.heading}
                </h3>
                <ul className="space-y-0.5">
                  {bucket.items.map((call) => {
                    const conv = conversations.find((c) => c.id === call.conversationId);
                    const isOutgoing = call.startedBy === me?.id;
                    const otherUserId = conv?.participants.find((p) => p.id !== me?.id)?.id;
                    const other = otherUserId ? users.find((u) => u.id === otherUserId) : null;
                    const isMissed =
                      !isOutgoing &&
                      !call.participants.some(
                        (p) => p.userId === me?.id && p.joinedAt,
                      );

                    const title =
                      conv?.type === 'dm'
                        ? other?.displayName ?? other?.email ?? 'Unknown'
                        : conv?.name ?? 'Group call';

                    const avatarKey =
                      conv?.type === 'dm' && other
                        ? {
                            id: other.id,
                            displayName: other.displayName,
                            email: other.email,
                          }
                        : { id: call.conversationId, displayName: title, email: title };

                    const isVideo = call.callType === 'video';
                    const directionLabel = isMissed
                      ? 'Missed'
                      : isOutgoing
                        ? 'Outgoing'
                        : 'Incoming';
                    const callbackKey = `${call.conversationId}:`;

                    return (
                      <li key={call.id} className="group relative">
                        <div
                          onClick={() => selectConversation(call.conversationId)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') selectConversation(call.conversationId);
                          }}
                          className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-zinc-900/60 rounded-xl transition-all duration-300 ease-spring cursor-pointer"
                        >
                          <Avatar
                            id={avatarKey.id}
                            displayName={avatarKey.displayName}
                            email={avatarKey.email}
                            size={40}
                            kind={conv?.type === 'dm' ? 'user' : 'conversation'}
                            avatarUpdatedAt={
                              conv?.type === 'dm' ? undefined : conv?.avatarUpdatedAt ?? null
                            }
                          />
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-sm font-medium truncate ${
                                isMissed ? 'text-rose-300' : 'text-zinc-100'
                              }`}
                            >
                              {title}
                            </p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <CallDirectionIcon
                                kind={isMissed ? 'missed' : isOutgoing ? 'out' : 'in'}
                              />
                              <span
                                className={`text-[11px] ${
                                  isMissed ? 'text-rose-400/80' : 'text-zinc-500'
                                }`}
                              >
                                {directionLabel} {isVideo ? 'video' : 'voice'} call
                                {call.durationSeconds !== null && !isMissed
                                  ? ` · ${formatDuration(call.durationSeconds)}`
                                  : ''}
                              </span>
                            </div>
                          </div>
                          <span className="text-[11px] text-zinc-500 tabular-nums shrink-0 group-hover:opacity-0 transition-opacity duration-200">
                            {relativeTime(call.startedAt)}
                          </span>
                        </div>
                        {/* Hover-revealed call-back actions, absolutely positioned over the timestamp. */}
                        <div className="absolute top-1/2 -translate-y-1/2 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-events-none group-hover:pointer-events-auto group-focus-within:pointer-events-auto transition-opacity duration-200">
                          <CallBackButton
                            onClick={(e) => {
                              e.stopPropagation();
                              void callBack(call.conversationId, false);
                            }}
                            title="Voice call back"
                            pending={pendingCallback === `${callbackKey}a`}
                          >
                            <IconPhone size={13} strokeWidth={1.5} />
                          </CallBackButton>
                          <CallBackButton
                            onClick={(e) => {
                              e.stopPropagation();
                              void callBack(call.conversationId, true);
                            }}
                            title="Video call back"
                            pending={pendingCallback === `${callbackKey}v`}
                          >
                            <IconVideo size={13} strokeWidth={1.5} />
                          </CallBackButton>
                          <CallBackButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setDetailsCall(call);
                            }}
                            title="Call details"
                            pending={false}
                          >
                            <IconMoreVertical size={13} strokeWidth={1.5} />
                          </CallBackButton>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>

      {newChatOpen && <NewChatModal onClose={() => setNewChatOpen(false)} />}
      {detailsCall && (
        <CallDetailsModal
          call={detailsCall}
          onClose={() => setDetailsCall(null)}
          onCallBack={(video) => {
            setDetailsCall(null);
            void callBack(detailsCall.conversationId, video);
          }}
          onOpenConversation={() => {
            setDetailsCall(null);
            selectConversation(detailsCall.conversationId);
          }}
        />
      )}
    </div>
  );
}

function CallDirectionIcon({ kind }: { kind: 'in' | 'out' | 'missed' }) {
  if (kind === 'missed') {
    return (
      <span className="text-rose-400">
        <IconPhoneMissed size={11} strokeWidth={1.75} />
      </span>
    );
  }
  if (kind === 'out') {
    return (
      <span className="text-emerald-400">
        <IconPhoneOutgoing size={11} strokeWidth={1.75} />
      </span>
    );
  }
  return (
    <span className="text-indigo-400">
      <IconPhoneIncoming size={11} strokeWidth={1.75} />
    </span>
  );
}

function CallBackButton({
  onClick,
  title,
  pending,
  children,
}: {
  onClick: (e: React.MouseEvent) => void;
  title: string;
  pending: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={pending}
      title={title}
      aria-label={title}
      className="w-8 h-8 rounded-xl glass-chrome hairline text-zinc-300 hover:text-zinc-50 hover:hairline-strong disabled:opacity-50 transition-all duration-300 ease-spring active:scale-[0.94] flex items-center justify-center"
    >
      {pending ? (
        <span className="w-3 h-3 rounded-full border border-zinc-400 border-t-transparent animate-spin" />
      ) : (
        children
      )}
    </button>
  );
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

interface CallDetailsModalProps {
  call: CallLogEntry;
  onClose: () => void;
  onCallBack: (video: boolean) => void;
  onOpenConversation: () => void;
}

function CallDetailsModal({
  call,
  onClose,
  onCallBack,
  onOpenConversation,
}: CallDetailsModalProps) {
  const me = useChat((s) => s.me);
  const users = useChat((s) => s.users);
  const conversations = useChat((s) => s.conversations);
  const conv = conversations.find((c) => c.id === call.conversationId);
  const starter = users.find((u) => u.id === call.startedBy);
  const isVideo = call.callType === 'video';

  // Esc to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const title =
    conv?.type === 'dm'
      ? users.find((u) => u.id === conv.participants.find((p) => p.id !== me?.id)?.id)
          ?.displayName ??
        users.find((u) => u.id === conv.participants.find((p) => p.id !== me?.id)?.id)
          ?.email ??
        'Direct call'
      : conv?.name ?? 'Group call';

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-up"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 rounded-xl text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition flex items-center justify-center"
        >
          <IconClose size={13} />
        </button>

        <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">
          Call details
        </p>
        <h2 className="text-lg font-semibold tracking-tight truncate pr-8">{title}</h2>
        <p className="text-xs text-zinc-400 mt-1">
          {isVideo ? 'Video' : 'Voice'} call ·{' '}
          {call.durationSeconds !== null
            ? formatDuration(call.durationSeconds)
            : 'no answer'}{' '}
          · started {relativeTime(call.startedAt)}
        </p>

        <dl className="mt-5 space-y-2 text-xs">
          <Row label="Started by">
            {starter?.displayName ?? starter?.email ?? 'Unknown'}
            {call.startedBy === me?.id && (
              <span className="text-zinc-500 ml-1">(you)</span>
            )}
          </Row>
          <Row label="Type">{isVideo ? 'Video' : 'Voice'}</Row>
          <Row label="Started">
            {new Date(call.startedAt).toLocaleString()}
          </Row>
          {call.endedAt && (
            <Row label="Ended">{new Date(call.endedAt).toLocaleString()}</Row>
          )}
          {call.durationSeconds !== null && (
            <Row label="Duration">{formatDuration(call.durationSeconds)}</Row>
          )}
        </dl>

        <p className="mt-5 mb-2 text-[10px] uppercase tracking-[0.14em] text-zinc-500">
          Participants ({call.participants.length})
        </p>
        <ul className="space-y-1.5 max-h-48 overflow-y-auto rounded-md border border-zinc-900 bg-zinc-900/30 p-2">
          {call.participants.map((p) => {
            const u = users.find((x) => x.id === p.userId);
            const name = u?.displayName ?? u?.email ?? p.userId;
            const joined = p.joinedAt
              ? `joined ${relativeTime(p.joinedAt)}`
              : 'never joined';
            const left = p.leftAt ? `left ${relativeTime(p.leftAt)}` : '';
            return (
              <li
                key={p.userId}
                className="flex items-center gap-2 px-2 py-1 rounded text-[11px]"
              >
                <Avatar id={p.userId} displayName={u?.displayName ?? null} email={u?.email ?? '?'} size={20} />
                <span className="flex-1 min-w-0 truncate">{name}</span>
                <span className="text-zinc-500 shrink-0">
                  {joined}
                  {left && ` · ${left}`}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="mt-5 flex items-center gap-2">
          <button
            onClick={() => onCallBack(false)}
            className="flex-1 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 text-xs font-medium px-3 py-2 transition flex items-center justify-center gap-2"
          >
            <IconPhone size={13} strokeWidth={1.5} />
            Voice call back
          </button>
          <button
            onClick={() => onCallBack(true)}
            className="flex-1 rounded-md bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-200 text-xs font-medium px-3 py-2 transition flex items-center justify-center gap-2"
          >
            <IconVideo size={13} strokeWidth={1.5} />
            Video call back
          </button>
        </div>
        <button
          onClick={onOpenConversation}
          className="mt-2 w-full rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 text-xs px-3 py-2 transition"
        >
          Open conversation
        </button>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-zinc-500 shrink-0">{label}</dt>
      <dd className="text-zinc-200 truncate text-right min-w-0">{children}</dd>
    </div>
  );
}
